// The account's name and picture: how the person shows in the workspaces.
// Where they live is the host's to say (`extensions.account`): this server's
// own profile (cloud), a source that reaches them elsewhere (a Hub tied to a
// cloud account reads and writes that account through itself), or nowhere
// (the offline edition on its own: no one else sees its owner).
//
// Promise:
// - `useAccount()` is undefined while it loads, null where there is no
//   account here now (General then has neither name nor picture), else the
//   name and picture.
// - `useShownAccount()` is what the shell shows for the person (the sidebar's
//   switcher, the phone's menu, the app's header): the account where there is
//   one, else this server's own profile's name with initials.
// - The saves change the account where it lives and read it again.
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi, useMeApp } from "../app/context";
import type { Api } from "../data/api";
import { useProfile, useProfilePicture, useUpdateProfile } from "./profile";

export interface AccountView {
  name: string;
  /** The picture's URL, or null for initials. */
  image: string | null;
  /** A picture of its own is set (it can be removed). */
  hasPicture: boolean;
}

/** The account reached elsewhere, through this server. */
export interface AccountSource {
  /** The account, or null when there is none now (not tied). */
  read(api: Api): Promise<AccountView | null>;
  setName(api: Api, name: string): Promise<unknown>;
  setPicture(api: Api, picture: Blob): Promise<unknown>;
  removePicture(api: Api): Promise<unknown>;
}

/** `profile`: this server's own profile is the account (cloud). */
export type AccountPlace = "profile" | AccountSource;

export const accountKey = ["me", "account"] as const;

const sourceQuery = (api: Api, source: AccountSource) =>
  queryOptions({
    queryKey: accountKey,
    queryFn: () => source.read(api),
  });

function useProfileAccount(_place: AccountPlace | undefined) {
  const profile = useProfile().data;
  const image = useProfilePicture();
  return profile
    ? { name: profile.name, image, hasPicture: profile.imageFileId !== null }
    : undefined;
}

function useSourceAccount(
  place: AccountPlace | undefined,
): AccountView | null | undefined {
  const source = place as AccountSource;
  const query = useQuery(sourceQuery(useApi(), source));
  // Out of reach reads as no account: nothing to change it with now.
  return query.isError ? null : query.data;
}

const useNoAccount = (_place: AccountPlace | undefined): null => null;

export function useAccount(): AccountView | null | undefined {
  const place = useMeApp().extensions.account;
  // The host's choice, the same for the app's life.
  const useOne =
    place === "profile"
      ? useProfileAccount
      : place
        ? useSourceAccount
        : useNoAccount;
  return useOne(place);
}

/** Who the shell shows for the person: the account, else the own profile's name. */
export function useShownAccount(): {
  name: string;
  image: string | null;
  seed: string | undefined;
} {
  const profile = useProfile().data;
  const account = useAccount();
  return {
    name: account?.name ?? profile?.name ?? "",
    image: account?.image ?? null,
    seed: profile?.id,
  };
}

/**
 * The account's saves, where it lives. `picture` goes to PictureField: the
 * own profile takes the file it uploads here; a source takes the image itself
 * (it lives elsewhere).
 */
export function useAccountSaves() {
  const place = useMeApp().extensions.account;
  const source = typeof place === "object" ? place : null;
  const api = useApi();
  const qc = useQueryClient();
  const update = useUpdateProfile();
  const reread = () => qc.invalidateQueries({ queryKey: accountKey });
  const name = useMutation({
    mutationFn: (next: string) =>
      source ? source.setName(api, next) : update.mutateAsync({ name: next }),
    onSettled: () => (source ? reread() : undefined),
  });
  return {
    name,
    picture: source
      ? {
          save: async (picture: Blob) => {
            await source.setPicture(api, picture);
            await reread();
          },
        }
      : {
          onPicked: (imageFileId: string) =>
            update.mutateAsync({ imageFileId }),
        },
    async removePicture(): Promise<void> {
      if (!source) {
        await update.mutateAsync({ imageFileId: null });
        return;
      }
      await source.removePicture(api);
      await reread();
    },
  };
}
